import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const FeatureFlagsContext = createContext();

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('feature_flags')
        .select('name, is_enabled')
        .eq('is_enabled', true); // Only fetch enabled flags due to RLS policy

      if (error) {
        throw error;
      }

      // Convert array to object for easier access
      const flagsObject = {};
      data.forEach(flag => {
        flagsObject[flag.name] = flag.is_enabled;
      });
      
      setFeatureFlags(flagsObject);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError(err.message);
      // Set default values in case of error
      setFeatureFlags({
        enable_sign_up: false, // Default to false for safety
        mfa_available: false, // Default to false for safety
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatureFlags();
  }, []);

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    return featureFlags[featureName] === true;
  };

  // Helper function to check if sign up is enabled
  const isSignUpEnabled = () => {
    return isFeatureEnabled('enable_sign_up');
  };

  // Helper function to check if MFA is available
  const isMfaAvailable = () => {
    return isFeatureEnabled('mfa_available');
  };

  const value = {
    featureFlags,
    loading,
    error,
    isFeatureEnabled,
    isSignUpEnabled,
    isMfaAvailable,
    refetch: fetchFeatureFlags,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
